import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Copy, GraduationCap, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import type { Slide as SlideType } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

import { Row } from './Row';
import { ScaledStage } from './ScaledStage';

interface Props {
  slide: SlideType;
  index: number;
  total: number;
}

export function Slide({ slide, index, total }: Props) {
  const isSelected = useEditorStore(
    (s) => s.selection?.kind === 'slide' && s.selection.slideId === slide.id,
  );
  const isFocusedFamily = useEditorStore(
    (s) => s.selection && 'slideId' in s.selection && s.selection.slideId === slide.id,
  );
  const actions = useEditorActions();

  const { setNodeRef, isOver } = useDroppable({
    id: `slide:${slide.id}`,
    data: { kind: 'slideBody', slideId: slide.id },
  });

  return (
    <div className="group/slide relative">
      {/* slide actions strip (only on hover) */}
      <div className="absolute -right-1 -top-9 flex items-center gap-1 opacity-0 transition group-hover/slide:opacity-100">
        <span className="mr-1 font-mono text-[10px] tracking-wider text-fg-subtle">
          {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
        <button
          onClick={() => useEditorStore.getState().setTeachingSlideId(slide.id)}
          className="inline-flex h-7 items-center gap-1.5 rounded-md bg-accent px-2.5 text-[11px] font-semibold uppercase tracking-wider text-white shadow-pill transition hover:opacity-90"
          title="Teach this slide"
        >
          <GraduationCap size={13} /> Teach
        </button>
        <button
          onClick={() => {
            useEditorStore.getState().setSelection({ kind: 'slide', slideId: slide.id });
            actions.duplicateSelection();
          }}
          className="grid h-7 w-7 place-items-center rounded-md bg-white text-fg-muted shadow-pill ring-1 ring-border hover:text-fg"
          title="Duplicate slide"
        >
          <Copy size={13} />
        </button>
        <button
          onClick={() => actions.removeSlide(slide.id)}
          disabled={total <= 1}
          className="grid h-7 w-7 place-items-center rounded-md bg-white text-fg-muted shadow-pill ring-1 ring-border hover:text-red-500 disabled:opacity-30"
          title="Delete slide"
        >
          <Trash2 size={13} />
        </button>
      </div>

      {/* the slide canvas — a fixed 16:9 design frame (DESIGN_CANVAS) scaled to
          the column width. Rows render in design space; slide-level chrome
          (label, add-row) is overlaid at UI scale so it stays a constant size. */}
      <ScaledStage
        wrapperRef={setNodeRef}
        onClick={() =>
          useEditorStore.getState().setSelection({ kind: 'slide', slideId: slide.id })
        }
        background={slide.background ?? '#fff'}
        className={cn(
          'rounded-2xl shadow-slide ring-1 ring-black/5 transition',
          (isSelected || isFocusedFamily) && 'shadow-slideSelected ring-2 ring-accent',
          isOver && !isSelected && 'ring-2 ring-accent/60',
        )}
        overlay={
          <>
            {/* INTRODUCTION pill */}
            <div className="absolute left-6 top-5 z-10 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
              <input
                value={slide.label}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => actions.updateSlide(slide.id, { label: e.target.value })}
                className="bg-transparent uppercase tracking-[0.12em] outline-none placeholder:text-fg-subtle/60 focus:text-fg"
                style={{ width: `${Math.max(slide.label.length, 6)}ch` }}
              />
              <span className="h-1.5 w-1.5 rounded-full bg-success" title="Slide is active" />
            </div>

            {/* trailing add row */}
            {slide.rows.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  actions.addRow(slide.id, 1);
                }}
                className="absolute bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full border border-border bg-white px-3 py-1 text-[11px] text-fg-muted opacity-0 shadow-pill transition group-hover/slide:opacity-100"
              >
                <Plus size={12} /> Add row
              </button>
            )}
          </>
        }
      >
        <div className="flex min-h-[720px] w-full flex-col pt-12">
          <SortableContext
            items={slide.rows.map((r) => r.id)}
            strategy={verticalListSortingStrategy}
          >
            {slide.rows.map((row) => (
              <Row key={row.id} row={row} slideId={slide.id} />
            ))}
          </SortableContext>

          {slide.rows.length === 0 && (
            <div className="flex flex-1 items-center justify-center">
              <Button
                variant="ghost"
                onClick={() => actions.addRow(slide.id, 1)}
                className="text-fg-subtle"
              >
                <Plus size={14} /> Add a row
              </Button>
            </div>
          )}
        </div>
      </ScaledStage>
    </div>
  );
}
